import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, Chip } from "@/components/Shared";
import { Button } from "@/components/ui/button";
import {
    Buildings, Users as UsersIcon, ChatCircleDots, Wrench, Storefront, WarningCircle,
    CheckCircle, ClockCounterClockwise, CalendarBlank, ArrowRight, Receipt, TrendUp,
    Wallet, PlusCircle, Toolbox, FileText,
} from "@phosphor-icons/react";

const inr = (n) => new Intl.NumberFormat("en-IN", { style: "currency", currency: "INR", maximumFractionDigits: 0 }).format(n || 0);
const todayISO = () => new Date().toISOString().slice(0, 10);
const timeAgo = (iso) => {
    try {
        const d = new Date(iso);
        const now = new Date();
        const sameDay = d.toDateString() === now.toDateString();
        if (sameDay) return d.toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit" });
        return d.toLocaleDateString("en-IN", { day: "2-digit", month: "short" });
    } catch { return ""; }
};

export default function Dashboard() {
    const { user } = useAuth();
    const isStaff = user?.role === "admin" || user?.role === "committee";

    return (
        <div className="animate-fade-up">
            <PageHeader
                overline={`${user?.role} · society command center`}
                title="Overview"
                description={isStaff
                    ? "What's happening in your society right now — and what needs your attention."
                    : "Your maintenance, requests and bookings at a glance."}
            />
            {isStaff ? <AdminCommandCenter /> : <ResidentOverview user={user} />}
        </div>
    );
}

/* ============================== ADMIN / COMMITTEE ============================== */

function AdminCommandCenter() {
    const [loading, setLoading] = useState(true);
    const [stats, setStats] = useState(null);
    const [cSummary, setCSummary] = useState(null);
    const [woSummary, setWoSummary] = useState(null);
    const [staffSummary, setStaffSummary] = useState(null);
    const [defSummary, setDefSummary] = useState(null);
    const [amSummary, setAmSummary] = useState(null);
    const [complaintsList, setComplaintsList] = useState([]);
    const [woList, setWoList] = useState([]);
    const [bookingsList, setBookingsList] = useState([]);

    useEffect(() => {
        (async () => {
            setLoading(true);
            const results = await Promise.allSettled([
                api.get("/stats"),
                api.get("/complaints/summary"),
                api.get("/work-orders/summary"),
                api.get("/staff/summary"),
                api.get("/defaulters/summary"),
                api.get("/amenities/summary"),
                api.get("/complaints"),
                api.get("/work-orders"),
                api.get("/bookings"),
            ]);
            const val = (i) => (results[i].status === "fulfilled" ? results[i].value.data : null);
            setStats(val(0));
            setCSummary(val(1));
            setWoSummary(val(2));
            setStaffSummary(val(3));
            setDefSummary(val(4));
            setAmSummary(val(5));
            setComplaintsList(val(6) || []);
            setWoList(val(7) || []);
            setBookingsList(val(8) || []);
            setLoading(false);
        })();
    }, []);

    const needsAttention = useMemo(() => {
        const items = [];
        if (cSummary?.open_over_15_days > 0) items.push({
            key: "overdue-complaints", severity: "danger", count: cSummary.open_over_15_days,
            title: `${cSummary.open_over_15_days} overdue complaint${cSummary.open_over_15_days > 1 ? "s" : ""}`,
            desc: "These complaints have exceeded the 15-day ageing threshold.",
            actionLabel: "View Complaints", to: "/app/complaints",
        });
        if (cSummary?.unassigned_open > 0) items.push({
            key: "unassigned-complaints", severity: "warn", count: cSummary.unassigned_open,
            title: `${cSummary.unassigned_open} unassigned complaint${cSummary.unassigned_open > 1 ? "s" : ""}`,
            desc: "Complaints waiting for staff/vendor assignment.",
            actionLabel: "Assign Now", to: "/app/complaints",
        });
        if (woSummary?.overdue > 0) items.push({
            key: "overdue-wo", severity: "danger", count: woSummary.overdue,
            title: `${woSummary.overdue} overdue work order${woSummary.overdue > 1 ? "s" : ""}`,
            desc: "Work orders past their due date.",
            actionLabel: "View Work Orders", to: "/app/work-orders",
        });
        if (woSummary?.unassigned_open > 0) items.push({
            key: "unassigned-wo", severity: "warn", count: woSummary.unassigned_open,
            title: `${woSummary.unassigned_open} unassigned work order${woSummary.unassigned_open > 1 ? "s" : ""}`,
            desc: "Work orders waiting for a staff/vendor assignment.",
            actionLabel: "Assign Now", to: "/app/work-orders",
        });
        if (amSummary?.pending_refunds > 0) items.push({
            key: "pending-refunds", severity: "warn", count: amSummary.pending_refunds,
            title: `${amSummary.pending_refunds} pending amenity refund${amSummary.pending_refunds > 1 ? "s" : ""}`,
            desc: "Refunds marked REFUND_DUE and not yet processed.",
            actionLabel: "View Bookings", to: "/app/amenities",
        });
        return items;
    }, [cSummary, woSummary, amSummary]);

    const recentActivity = useMemo(() => {
        const items = [];
        const complaintLabel = (c) => {
            switch (c.status) {
                case "reopened": return "reopened";
                case "resolved": return "marked resolved — awaiting resident confirmation";
                case "closed": return "closed";
                case "in_progress": return "moved to in progress";
                case "on_hold": return "put on hold";
                case "assigned": return `assigned${c.assigned_staff?.name ? " to " + c.assigned_staff.name : ""}`;
                case "acknowledged": return "acknowledged";
                default: return "raised";
            }
        };
        complaintsList.forEach((c) => {
            const ts = c.updated_at || c.created_at;
            if (!ts) return;
            items.push({ ts, kind: "complaint", text: `Complaint ${c.complaint_number || ""} ${complaintLabel(c)}`.trim(), to: "/app/complaints" });
        });
        const woLabel = (w) => {
            switch (w.status) {
                case "COMPLETED": return "completed";
                case "CLOSED": return "closed";
                case "CANCELLED": return "cancelled";
                case "IN_PROGRESS": return "moved to in progress";
                case "ON_HOLD": return "put on hold";
                case "ASSIGNED": return `assigned to ${w.assigned_provider_name || "a provider"}`;
                default: return "opened";
            }
        };
        woList.forEach((w) => {
            const ts = w.updated_at || w.created_at;
            if (!ts) return;
            items.push({ ts, kind: "wo", text: `Work Order ${w.work_order_number || ""} ${woLabel(w)}`.trim(), to: "/app/work-orders" });
        });
        bookingsList.forEach((b) => {
            if (b.status === "cancelled" && b.cancelled_at) {
                items.push({ ts: b.cancelled_at, kind: "booking", text: `${b.amenity_name || "Amenity"} booking cancelled${b.flat_label ? " — " + b.flat_label : ""}`, to: "/app/amenities" });
            } else if (b.created_at) {
                items.push({ ts: b.created_at, kind: "booking", text: `${b.amenity_name || "Amenity"} booking confirmed${b.flat_label ? " — " + b.flat_label : ""}`, to: "/app/amenities" });
            }
        });
        return items.filter(i => i.ts).sort((a, b) => new Date(b.ts) - new Date(a.ts)).slice(0, 8);
    }, [complaintsList, woList, bookingsList]);

    const todaySchedule = useMemo(() => {
        const today = todayISO();
        const items = [];
        bookingsList.filter(b => b.date === today && b.status !== "cancelled").forEach(b => {
            items.push({ timed: true, sortKey: b.start_time, time: `${b.start_time} – ${b.end_time}`, text: `${b.amenity_name || "Amenity"} booking${b.flat_label ? " — " + b.flat_label : ""}`, to: "/app/amenities" });
        });
        woList.filter(w => (w.due_date || "").slice(0, 10) === today && !["COMPLETED", "CLOSED", "CANCELLED"].includes(w.status)).forEach(w => {
            items.push({ timed: false, sortKey: "99:99", time: "Due today", text: `${w.title || "Work Order"} — ${w.work_order_number || ""}`.trim(), to: "/app/work-orders" });
        });
        return items.sort((a, b) => (a.sortKey || "").localeCompare(b.sortKey || ""));
    }, [bookingsList, woList]);

    const collectionHealthPct = useMemo(() => {
        if (!stats) return null;
        const total = (stats.total_collected || 0) + (stats.total_pending || 0);
        if (total <= 0) return null;
        return Math.round((stats.total_collected / total) * 100);
    }, [stats]);

    return (
        <div className="space-y-10" data-testid="admin-command-center">
            {/* 1. SOCIETY SNAPSHOT */}
            <section data-testid="society-snapshot-section">
                <SectionLabel>Society Snapshot</SectionLabel>
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
                    <SnapshotCard testId="snapshot-flats" to="/app/flats" icon={Buildings} label="Total Flats" value={loading ? "—" : stats?.total_flats ?? 0} />
                    <SnapshotCard testId="snapshot-residents" to="/app/directory" icon={UsersIcon} label="Total Residents" value={loading ? "—" : stats?.total_residents ?? 0} />
                    <SnapshotCard testId="snapshot-collection" to="/app/financial-reports" icon={TrendUp} label="Collected" value={loading ? "—" : inr(stats?.total_collected)} />
                    <SnapshotCard testId="snapshot-outstanding" to="/app/defaulters" icon={Wallet} label="Outstanding" value={loading ? "—" : inr(stats?.total_pending)} accent />
                    <SnapshotCard testId="snapshot-complaints" to="/app/complaints" icon={ChatCircleDots} label="Open Complaints" value={loading ? "—" : cSummary?.total_open ?? stats?.complaints_open ?? 0} />
                    <SnapshotCard testId="snapshot-workorders" to="/app/work-orders" icon={Wrench} label="Active Work Orders" value={loading ? "—" : woSummary?.total_open ?? 0} />
                </div>
            </section>

            {/* 2. NEEDS ATTENTION */}
            <section data-testid="needs-attention-section">
                <SectionLabel>Needs Attention</SectionLabel>
                {loading ? (
                    <div className="bg-white border border-brand-line rounded-sm p-6 text-sm text-brand-inkSoft">Checking for items that need action…</div>
                ) : needsAttention.length === 0 ? (
                    <div data-testid="needs-attention-empty" className="bg-white border border-brand-line rounded-sm p-6 flex items-center gap-3">
                        <CheckCircle size={22} className="text-[#059669]" weight="fill" />
                        <p className="text-brand-ink font-medium">Everything looks good — no urgent actions right now.</p>
                    </div>
                ) : (
                    <div className="grid md:grid-cols-2 gap-4">
                        {needsAttention.map((item) => (
                            <div key={item.key} data-testid={`needs-attention-${item.key}`} className={`bg-white border rounded-lg p-5 flex items-start gap-3 ${item.severity === "danger" ? "border-[#FECACA]" : "border-[#FFEDD5]"}`}>
                                <WarningCircle size={22} weight="fill" className={item.severity === "danger" ? "text-[#DC2626] shrink-0 mt-0.5" : "text-[#EA580C] shrink-0 mt-0.5"} />
                                <div className="flex-1">
                                    <div className="flex items-center gap-2">
                                        <p className="font-heading text-lg text-brand-ink">{item.title}</p>
                                        <Chip variant={item.severity === "danger" ? "danger" : "warn"}>{item.severity === "danger" ? "Overdue" : "Action needed"}</Chip>
                                    </div>
                                    <p className="text-sm text-brand-inkSoft mt-1">{item.desc}</p>
                                    <Link to={item.to} data-testid={`needs-attention-action-${item.key}`} className="inline-flex items-center gap-1 text-xs uppercase tracking-overline text-brand-action hover:underline mt-3 font-medium">
                                        {item.actionLabel} <ArrowRight size={12} />
                                    </Link>
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </section>

            <div className="grid lg:grid-cols-2 gap-6">
                {/* 3. FINANCE SNAPSHOT */}
                <section data-testid="finance-snapshot-section" className="bg-white border border-brand-line rounded-sm p-6">
                    <div className="flex items-center justify-between mb-4">
                        <h3 className="font-heading text-xl text-brand-ink">Finance Snapshot</h3>
                        <Link to="/app/financial-reports" data-testid="finance-view-reports-link" className="text-xs uppercase tracking-overline text-brand-action hover:underline inline-flex items-center gap-1">View Financial Reports <ArrowRight size={12} /></Link>
                    </div>
                    <div className="space-y-3">
                        <FinRow label="Collected (all-time)" value={inr(stats?.total_collected)} />
                        <FinRow label="Outstanding" value={inr(stats?.total_pending)} warn />
                        <FinRow label="Expenses logged" value={inr(stats?.total_expenses)} />
                        <FinRow label="Defaulter flats" value={`${defSummary?.total_defaulters ?? 0} · ${inr(defSummary?.total_outstanding)}`} warn={(defSummary?.total_defaulters ?? 0) > 0} />
                        {collectionHealthPct !== null && (
                            <div className="pt-2">
                                <div className="flex items-center justify-between text-xs text-brand-inkSoft mb-1">
                                    <span>Collection health</span><span className="font-medium text-brand-ink">{collectionHealthPct}%</span>
                                </div>
                                <div className="h-1.5 bg-brand-bg rounded-full overflow-hidden">
                                    <div className="h-full bg-brand-action rounded-full" style={{ width: `${collectionHealthPct}%` }} />
                                </div>
                            </div>
                        )}
                    </div>
                </section>

                {/* 4. OPERATIONS SNAPSHOT */}
                <section data-testid="operations-snapshot-section" className="bg-white border border-brand-line rounded-sm p-6">
                    <h3 className="font-heading text-xl text-brand-ink mb-4">Operations Snapshot</h3>
                    <div className="grid grid-cols-2 gap-4">
                        <OpsCard testId="ops-complaints-card" to="/app/complaints" icon={ChatCircleDots} title="Complaints">
                            <OpsLine label="New" value={cSummary?.by_status?.new ?? 0} />
                            <OpsLine label="In progress" value={cSummary?.by_status?.in_progress ?? 0} />
                            <OpsLine label="Overdue" value={cSummary?.open_over_15_days ?? 0} warn />
                        </OpsCard>
                        <OpsCard testId="ops-workorders-card" to="/app/work-orders" icon={Wrench} title="Work Orders">
                            <OpsLine label="Open" value={woSummary?.by_status?.OPEN ?? 0} />
                            <OpsLine label="In progress" value={woSummary?.by_status?.IN_PROGRESS ?? 0} />
                            <OpsLine label="Overdue" value={woSummary?.overdue ?? 0} warn />
                        </OpsCard>
                        <OpsCard testId="ops-staff-card" to="/app/staff" icon={Storefront} title="Staff & Vendors">
                            <OpsLine label="Active" value={(staffSummary?.active_staff ?? 0) + (staffSummary?.active_vendors ?? 0)} />
                            <OpsLine label="Currently assigned" value={staffSummary?.currently_assigned ?? 0} />
                            <OpsLine label="Open assignments" value={staffSummary?.open_assignments ?? 0} />
                        </OpsCard>
                        <OpsCard testId="ops-amenities-card" to="/app/amenities" icon={CalendarBlank} title="Amenities">
                            <OpsLine label="Today's bookings" value={amSummary?.today_bookings ?? 0} />
                            <OpsLine label="Today's cancellations" value={amSummary?.today_cancellations ?? 0} />
                            <OpsLine label="Refunds pending" value={amSummary?.pending_refunds ?? 0} warn={(amSummary?.pending_refunds ?? 0) > 0} />
                        </OpsCard>
                    </div>
                </section>
            </div>

            <div className="grid lg:grid-cols-2 gap-6">
                {/* 5. RECENT ACTIVITY */}
                <section data-testid="recent-activity-section" className="bg-white border border-brand-line rounded-sm p-6">
                    <div className="flex items-center gap-2 mb-4">
                        <ClockCounterClockwise size={18} className="text-brand-inkSoft" />
                        <h3 className="font-heading text-xl text-brand-ink">Recent Activity</h3>
                    </div>
                    {loading ? (
                        <p className="text-sm text-brand-inkSoft">Loading…</p>
                    ) : recentActivity.length === 0 ? (
                        <p data-testid="recent-activity-empty" className="text-sm text-brand-inkSoft">No recent activity yet.</p>
                    ) : (
                        <div className="divide-y divide-brand-line/70">
                            {recentActivity.map((item, i) => (
                                <Link to={item.to} key={i} data-testid={`recent-activity-item-${i}`} className="flex items-start gap-3 py-3 group hover:bg-brand-bg/40 -mx-2 px-2 rounded-sm transition-colors">
                                    <span className="text-[11px] uppercase tracking-overline text-brand-inkSoft w-14 shrink-0 mt-0.5">{timeAgo(item.ts)}</span>
                                    <span className="text-sm text-brand-ink group-hover:text-brand-action transition-colors">{item.text}</span>
                                </Link>
                            ))}
                        </div>
                    )}
                </section>

                {/* 6. TODAY'S SCHEDULE */}
                <section data-testid="today-schedule-section" className="bg-white border border-brand-line rounded-sm p-6">
                    <div className="flex items-center gap-2 mb-4">
                        <CalendarBlank size={18} className="text-brand-inkSoft" />
                        <h3 className="font-heading text-xl text-brand-ink">Today's Schedule</h3>
                    </div>
                    {loading ? (
                        <p className="text-sm text-brand-inkSoft">Loading…</p>
                    ) : todaySchedule.length === 0 ? (
                        <p data-testid="today-schedule-empty" className="text-sm text-brand-inkSoft">No scheduled activities today.</p>
                    ) : (
                        <div className="divide-y divide-brand-line/70">
                            {todaySchedule.map((item, i) => (
                                <Link to={item.to} key={i} data-testid={`today-schedule-item-${i}`} className="flex items-start gap-3 py-3 group hover:bg-brand-bg/40 -mx-2 px-2 rounded-sm transition-colors">
                                    <span className={`text-[11px] uppercase tracking-overline w-24 shrink-0 mt-0.5 ${item.timed ? "text-brand-action font-medium" : "text-brand-inkSoft"}`}>{item.time}</span>
                                    <span className="text-sm text-brand-ink group-hover:text-brand-action transition-colors">{item.text}</span>
                                </Link>
                            ))}
                        </div>
                    )}
                </section>
            </div>

            {/* 7. QUICK ACTIONS */}
            <section data-testid="quick-actions-section">
                <SectionLabel>Quick Actions</SectionLabel>
                <div className="flex flex-wrap gap-3">
                    <QuickAction testId="quick-action-raise-complaint" to="/app/complaints" state={{ autoOpenCreate: true }} icon={ChatCircleDots} label="Raise Complaint" />
                    <QuickAction testId="quick-action-create-wo" to="/app/work-orders" state={{ autoOpenCreate: true }} icon={Toolbox} label="Create Work Order" />
                    <QuickAction testId="quick-action-add-staff" to="/app/staff" state={{ autoOpenCreate: true }} icon={PlusCircle} label="Add Staff / Vendor" />
                    <QuickAction testId="quick-action-manage-amenities" to="/app/amenities" icon={CalendarBlank} label="Manage Amenities" />
                    <QuickAction testId="quick-action-financial-reports" to="/app/financial-reports" icon={FileText} label="View Financial Reports" />
                </div>
            </section>
        </div>
    );
}

/* ============================== RESIDENT ============================== */

function ResidentOverview({ user }) {
    const [loading, setLoading] = useState(true);
    const [stats, setStats] = useState(null);
    const [invoices, setInvoices] = useState([]);
    const [receipts, setReceipts] = useState([]);
    const [complaints, setComplaints] = useState([]);
    const [bookings, setBookings] = useState([]);
    const [announcements, setAnnouncements] = useState([]);

    useEffect(() => {
        (async () => {
            setLoading(true);
            const results = await Promise.allSettled([
                api.get("/stats"),
                api.get("/invoices"),
                api.get("/receipts"),
                api.get("/complaints"),
                api.get("/bookings"),
                api.get("/announcements"),
            ]);
            const val = (i) => (results[i].status === "fulfilled" ? results[i].value.data : null);
            setStats(val(0));
            setInvoices(val(1) || []);
            setReceipts(val(2) || []);
            setComplaints(val(3) || []);
            setBookings(val(4) || []);
            setAnnouncements((val(5) || []).slice(0, 3));
            setLoading(false);
        })();
    }, []);

    const unpaidInvoices = useMemo(() => invoices.filter(i => i.status !== "paid").sort((a, b) => (a.due_date || "").localeCompare(b.due_date || "")), [invoices]);
    const nextDue = unpaidInvoices[0];
    const lastReceipt = receipts[0];

    const myOpen = complaints.filter(c => ["new", "acknowledged", "assigned", "reopened"].includes(c.status));
    const myInProgress = complaints.filter(c => ["in_progress", "on_hold"].includes(c.status));
    const myPendingConfirm = complaints.filter(c => c.status === "resolved");
    const myResolved = complaints.filter(c => c.status === "closed").slice(0, 3);

    const today = todayISO();
    const upcomingBookings = bookings.filter(b => b.date >= today && b.status !== "cancelled").sort((a, b) => (a.date + a.start_time).localeCompare(b.date + b.start_time));
    const todaysBooking = upcomingBookings.find(b => b.date === today);

    return (
        <div className="space-y-8" data-testid="resident-overview">
            <div className="grid lg:grid-cols-2 gap-6">
                {/* MY FINANCE */}
                <section data-testid="resident-finance-card" className="bg-white border border-brand-line rounded-sm p-6">
                    <div className="flex items-center justify-between mb-4">
                        <h3 className="font-heading text-xl text-brand-ink">My Finance</h3>
                        <Link to="/app/invoices" className="text-xs uppercase tracking-overline text-brand-action hover:underline inline-flex items-center gap-1">All invoices <ArrowRight size={12} /></Link>
                    </div>
                    <div className="space-y-3">
                        <FinRow label="Outstanding amount" value={inr(stats?.my_pending_amount)} warn={(stats?.my_pending_amount ?? 0) > 0} />
                        <FinRow label="Unpaid invoices" value={stats?.my_unpaid_count ?? 0} />
                        <FinRow label="Next due date" value={nextDue ? new Date(nextDue.due_date).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" }) : "—"} />
                        <FinRow label="Last payment" value={lastReceipt ? `${inr(lastReceipt.amount)} on ${new Date(lastReceipt.payment_date || lastReceipt.created_at).toLocaleDateString("en-IN", { day: "2-digit", month: "short" })}` : "—"} />
                    </div>
                    {nextDue && (
                        <Link to={`/app/invoices/${nextDue.id}`} data-testid="resident-pay-maintenance-link" className="mt-4 inline-flex items-center gap-2 rounded-full bg-brand-action hover:bg-brand-actionHover text-white text-sm px-4 py-2 transition-colors">
                            <Wallet size={15} /> Pay Maintenance
                        </Link>
                    )}
                </section>

                {/* MY REQUESTS */}
                <section data-testid="resident-requests-card" className="bg-white border border-brand-line rounded-sm p-6">
                    <div className="flex items-center justify-between mb-4">
                        <h3 className="font-heading text-xl text-brand-ink">My Requests</h3>
                        <Link to="/app/complaints" className="text-xs uppercase tracking-overline text-brand-action hover:underline inline-flex items-center gap-1">All requests <ArrowRight size={12} /></Link>
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                        <MiniStat label="Open" value={myOpen.length} />
                        <MiniStat label="In progress" value={myInProgress.length} />
                        <MiniStat label="Pending confirmation" value={myPendingConfirm.length} warn={myPendingConfirm.length > 0} />
                        <MiniStat label="Recently resolved" value={myResolved.length} />
                    </div>
                    {myPendingConfirm.length > 0 && (
                        <p className="text-xs text-[#EA580C] mt-3">You have {myPendingConfirm.length} resolved complaint(s) awaiting your confirmation.</p>
                    )}
                </section>
            </div>

            <div className="grid lg:grid-cols-2 gap-6">
                {/* MY BOOKINGS */}
                <section data-testid="resident-bookings-card" className="bg-white border border-brand-line rounded-sm p-6">
                    <div className="flex items-center justify-between mb-4">
                        <h3 className="font-heading text-xl text-brand-ink">My Bookings</h3>
                        <Link to="/app/amenities" className="text-xs uppercase tracking-overline text-brand-action hover:underline inline-flex items-center gap-1">Book amenity <ArrowRight size={12} /></Link>
                    </div>
                    {todaysBooking && (
                        <div className="bg-brand-sage/30 border border-brand-action rounded-sm p-3 mb-3">
                            <p className="text-[10px] uppercase tracking-overline text-brand-action">Today</p>
                            <p className="text-sm font-medium text-brand-ink mt-1">{todaysBooking.amenity_name} · {todaysBooking.start_time}–{todaysBooking.end_time}</p>
                        </div>
                    )}
                    {upcomingBookings.length === 0 ? (
                        <p data-testid="resident-no-bookings" className="text-sm text-brand-inkSoft">No upcoming bookings.</p>
                    ) : (
                        <div className="divide-y divide-brand-line/70">
                            {upcomingBookings.slice(0, 4).map((b) => (
                                <div key={b.id} className="py-2.5 flex items-center justify-between text-sm">
                                    <span className="text-brand-ink">{b.amenity_name}</span>
                                    <span className="text-brand-inkSoft text-xs">{new Date(b.date).toLocaleDateString("en-IN", { day: "2-digit", month: "short" })} · {b.start_time}</span>
                                </div>
                            ))}
                        </div>
                    )}
                </section>

                {/* MY DOCUMENTS / INFORMATION */}
                <section data-testid="resident-documents-card" className="bg-white border border-brand-line rounded-sm p-6">
                    <div className="flex items-center justify-between mb-4">
                        <h3 className="font-heading text-xl text-brand-ink">My Documents</h3>
                        <Link to="/app/my-receipts" className="text-xs uppercase tracking-overline text-brand-action hover:underline inline-flex items-center gap-1">Receipts <ArrowRight size={12} /></Link>
                    </div>
                    {announcements.length === 0 ? (
                        <p className="text-sm text-brand-inkSoft">No notices yet.</p>
                    ) : (
                        <div className="space-y-3">
                            {announcements.map((a) => (
                                <div key={a.id} className="border-l-2 border-brand-action pl-3">
                                    <p className="font-medium text-brand-ink text-sm">{a.title}</p>
                                    <p className="text-xs text-brand-inkSoft mt-1 line-clamp-2">{a.content}</p>
                                </div>
                            ))}
                        </div>
                    )}
                </section>
            </div>

            {/* QUICK ACTIONS */}
            <section data-testid="resident-quick-actions">
                <SectionLabel>Quick Actions</SectionLabel>
                <div className="flex flex-wrap gap-3">
                    {nextDue && <QuickAction testId="quick-action-pay-maintenance" to={`/app/invoices/${nextDue.id}`} icon={Wallet} label="Pay Maintenance" />}
                    <QuickAction testId="quick-action-raise-complaint-resident" to="/app/complaints" state={{ autoOpenCreate: true }} icon={ChatCircleDots} label="Raise Complaint" />
                    <QuickAction testId="quick-action-book-amenity" to="/app/amenities" icon={CalendarBlank} label="Book Amenity" />
                    <QuickAction testId="quick-action-my-receipts" to="/app/my-receipts" icon={Receipt} label="My Receipts" />
                </div>
            </section>
        </div>
    );
}

/* ============================== Shared small pieces ============================== */

function SectionLabel({ children }) {
    return <p className="text-[10px] uppercase tracking-overline text-brand-action font-medium mb-3">{children}</p>;
}

function SnapshotCard({ testId, to, icon: Icon, label, value, accent }) {
    return (
        <Link to={to} data-testid={testId} className="bg-white border border-brand-line rounded-sm p-4 hover:-translate-y-1 hover:shadow-lg transition-transform duration-200 flex flex-col">
            <Icon size={18} className={accent ? "text-brand-action" : "text-brand-inkSoft"} />
            <p className={`font-heading text-2xl tracking-tight mt-3 ${accent ? "text-brand-action" : "text-brand-ink"}`}>{value}</p>
            <p className="text-[10px] uppercase tracking-overline text-brand-inkSoft mt-1">{label}</p>
        </Link>
    );
}

function FinRow({ label, value, warn }) {
    return (
        <div className="flex items-center justify-between">
            <span className="text-sm text-brand-inkSoft">{label}</span>
            <span className={`font-medium ${warn ? "text-brand-action" : "text-brand-ink"}`}>{value}</span>
        </div>
    );
}

function MiniStat({ label, value, warn }) {
    return (
        <div className="bg-brand-bg rounded-sm p-3">
            <p className={`font-heading text-2xl ${warn ? "text-brand-action" : "text-brand-ink"}`}>{value}</p>
            <p className="text-[10px] uppercase tracking-overline text-brand-inkSoft mt-1">{label}</p>
        </div>
    );
}

function OpsCard({ testId, to, icon: Icon, title, children }) {
    return (
        <Link to={to} data-testid={testId} className="border border-brand-line rounded-sm p-4 hover:border-brand-action transition-colors block">
            <div className="flex items-center gap-2 mb-2.5">
                <Icon size={15} className="text-brand-inkSoft" />
                <p className="text-xs font-medium text-brand-ink">{title}</p>
            </div>
            <div className="space-y-1.5">{children}</div>
        </Link>
    );
}

function OpsLine({ label, value, warn }) {
    return (
        <div className="flex items-center justify-between text-xs">
            <span className="text-brand-inkSoft">{label}</span>
            <span className={`font-medium ${warn && value > 0 ? "text-brand-action" : "text-brand-ink"}`}>{value}</span>
        </div>
    );
}

function QuickAction({ testId, to, state, icon: Icon, label }) {
    return (
        <Link to={to} state={state} data-testid={testId}>
            <Button variant="outline" className="rounded-full border-brand-line hover:border-brand-action hover:bg-brand-sage/40 text-brand-ink">
                <Icon size={15} className="mr-2" /> {label}
            </Button>
        </Link>
    );
}
